import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomLoginService } from '@privatelib/ngx18-authz-dev';
import { ROUTE_CONSTANTS } from '../../../util/route.constants';
import { ForgotPasswordService } from '../../../services/forgot-password/forgot-password.service';
import { CVANotificationService } from '../../../services/cva-notification/cva-notification.service';
import { CVA_CONSTANTS } from '../../../util/constants';
import { NotificationClassEnum } from '../../../models/notification-class.enum';
import { environment } from '../../../../environments/environment';

export interface UserDetails {
  id: string,
  email: string,
  firstName: string,
  lastName: string,
  providerId: string
}

export enum ENUM_PASSWORD_VISIBLE {
  NEW = 'new',
  C_NEW = 'c_new'
}

@Component({
    standalone: false,
    selector: 'cva-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrl: './reset-password.component.scss',
    
})


export class ResetPasswordComponent implements OnInit {

  token!: string | null;
  userId!: string | null;
  errorMessage!: string;
  isValidToken: boolean = false;
  afterChangePasswordSuccess: boolean = false;
  changePasswordForm!: FormGroup;
  newPasswordVisible: boolean = true;
  cNewPasswordVisible: boolean = true;
  isPasswordMatched: boolean = true;
  routes_constants = ROUTE_CONSTANTS;
  isPasswordExpired = false;
  cvaUI_url=environment.cvaUI_url

  constructor(
    private readonly router: Router,
    private readonly authService: CustomLoginService,
    private readonly route: ActivatedRoute,
    private readonly cvaNotificationService: CVANotificationService,
    private readonly forgotPasswordService: ForgotPasswordService) {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    this.route.queryParams.subscribe((params: any) => {
      this.token = params.token;
      this.userId = params.email;
      this.isPasswordExpired = params?.reason || false
    })
  }


  ngOnInit() {
    this.validateToken();
    this.changePasswordForm = new FormGroup({
      email: new FormControl({ value: this.userId, disabled: true }),
      new_password: new FormControl('', [Validators.required, Validators.minLength(8)]),
      c_new_password: new FormControl('', [Validators.required, Validators.minLength(8)])
    }, { validators: this.passwordMatchingValidator })
  }
  validateToken() {
    if (this.userId && this.token) {
      this.authService.validateResetPasswordToken(this.userId, this.token).subscribe({
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        next: (response: any) => {
          if (response.body?.valid) {
            this.isValidToken = true;
          } else {
            this.isValidToken = false;
            this.errorMessage = CVA_CONSTANTS.MESSAGE.INVALID_RESET_PASSWORD_LINK;
          }
        },
        error: () => {
          this.isValidToken = false;
          this.errorMessage = CVA_CONSTANTS.MESSAGE.INVALID_RESET_PASSWORD_LINK;
        }
      });
    } else {
      this.isValidToken = false;
      this.errorMessage = CVA_CONSTANTS.MESSAGE.INVALID_RESET_PASSWORD_LINK;
    }
  }


  gotoLogin() {
    this.router.navigate([ROUTE_CONSTANTS.LOGIN]);
  }


  passwordMatchingValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const password = control.get('new_password');
    const confirmPassword = control.get('c_new_password');

    return password?.value === confirmPassword?.value ? null : { notMatched: true };
  };

  submitForm() {
    if (!this.changePasswordForm.valid) {
      this.changePasswordForm.markAllAsTouched();
      return;
    }
    if (!this.validateConfirmPassword()) {
      this.cvaNotificationService.showNotification("Passwords does not match", NotificationClassEnum.ERROR);
      return;
    }
    if (!this.userId) {
      this.cvaNotificationService.showNotification("Invalid Email Id", NotificationClassEnum.ERROR);
      return;
    }
    if (!this.token) {
      this.cvaNotificationService.showNotification("Invalid Token", NotificationClassEnum.ERROR);
      return;
    }

    const formData = this.changePasswordForm.value;
    const userDetails = {
      password: formData.new_password,
      userId: this.userId,
      token: this.token
    };
    this.authService.resetPassword(userDetails).subscribe({
      next: () => {
        console.log("Password changed successfully");
        this.afterChangePasswordSuccess = true;
        this.router.navigate([ROUTE_CONSTANTS.LOGIN]);
        this.cvaNotificationService.showNotification(
          CVA_CONSTANTS.MESSAGE.CHANGE_PASSWORD_SUCCESS,
          NotificationClassEnum.SUCCESS
        );
      },
      // eslint-disable-next-line  @typescript-eslint/no-explicit-any
      error: (err: any) => {
        console.log('Error while changing the password', err);
        this.cvaNotificationService.showNotification(
          err.message || CVA_CONSTANTS.MESSAGE.CHANGE_PASSWORD_ERROR,
          NotificationClassEnum.ERROR
        );
      },

    })
  }

  isPasswordValid() {
    return this.changePasswordForm.get('new_password')?.valid;
  }

  setInputType(passwordVisible: string) {
    if (passwordVisible === ENUM_PASSWORD_VISIBLE.NEW) {
      this.newPasswordVisible = !this.newPasswordVisible;
    } else if (passwordVisible === ENUM_PASSWORD_VISIBLE.C_NEW) {
      this.cNewPasswordVisible = !this.cNewPasswordVisible;
    }
  }

  validateConfirmPassword() {
    const password = this.changePasswordForm.get('new_password');
    const confirmPassword = this.changePasswordForm.get('c_new_password');

    const matched = password && confirmPassword && password.value === confirmPassword.value
    this.isPasswordMatched = Boolean(matched);
    return matched
  }

  generateNewLink() {
    if (this.userId) {
      this.forgotPasswordService
        .forgotPassword(this.userId)
        .subscribe({
          next: (res: { success: boolean }) => {
            // Set the alert
            if (res?.success) {
              this.errorMessage = CVA_CONSTANTS.MESSAGE.FORGOT_PASSWORD_LINK_SENT;
              this.cvaNotificationService.showNotification(CVA_CONSTANTS.MESSAGE.FORGOT_PASSWORD_LINK_SENT, NotificationClassEnum.INFO, 5000)
            }

          }, error: (error) => {
            console.error("Password reset failed", error);
            this.cvaNotificationService.showNotification(CVA_CONSTANTS.MESSAGE.FORGOT_PASSWORD_ERROR, NotificationClassEnum.ERROR, 5000)
          }
        });
    }
  }

  //This function is just to avoid SONAR warning
  doNothing() {
    console.log("Do nothing");
  }

}
